import { useContext } from 'react';
import { 
    HStack, Text, Button, Icon, Menu, MenuButton, MenuList,
    Tooltip, Box
} from '@chakra-ui/react';
import { TbChevronDown, TbInfoCircle } from 'react-icons/tb';
import { MenuItemRow } from '../../../ui';
import SessionContext from '../../../contexts/SessionContext';
import { filterChartMetricsForInvoicing, orgShowsInvoicingReportingMetrics } from '../shared/invoicingGatedMetrics';

// Metrics that can be displayed in bar charts for domain tabs
export type ChartMetricType = 
    | 'orderVolume'
    | 'orderVolumeWoPM'
    | 'orderCost'
    | 'orderCount'
    | 'partsOrdered'
    | 'grossProfit'
    | 'avgListPrice'
    | 'medianListPrice'
    | 'avgOrderSize'
    | 'medianOrderSize'
    | 'avgGrossMarginPct'
    | 'medianGrossMarginPct'
    | 'p90GrossMarginPct'
    | 'avgGrossMarginPctWoPM'
    | 'medianGrossMarginPctWoPM'
    | 'grossMarginLossFromPM'
    | 'missedOpportunity'
    | 'avgMissedOpportunity'
    | 'medianMissedOpportunity'
    | 'partListPricePctOfOE'
    | 'pctAutoOrdered'
    | 'pctAutoOrderEnabled'
    | 'pctPartsFlagged'
    | 'avgPartValue'
    | 'medianPartValue'
    | 'totalQuotes'
    | 'avgQuotesPerOrder'
    | 'avgEtaChangeDays'
    | 'medianEtaChangeDays'
    | 'totalCostVariance'
    | 'avgCostVariance'
    | 'medianCostVariance'
    | 'avgDeliveryVarianceDays'
    | 'medianDeliveryVarianceDays'
    | 'pctPartsInvoiced'
    | 'quoteRate'
    | 'totalReturnCount'
    | 'totalReturnValue'
    | 'totalReturnPct'
    | 'pctOemVolume'
    | 'pctOemCount'
    | 'pctLkqVolume'
    | 'pctLkqCount'
    | 'pctAftermarketVolume'
    | 'pctAftermarketCount';

interface ChartMetricOption {
    value: ChartMetricType;
    label: string;
    format: 'currency' | 'percent' | 'number' | 'days';
    tooltip?: string;
}

export const CHART_METRIC_OPTIONS: ChartMetricOption[] = [
    { 
        value: 'orderVolume', 
        label: 'Order Volume', 
        format: 'currency',
        tooltip: 'Total dollars of parts ordered'
    },
    {
        value: 'orderVolumeWoPM',
        label: 'Order Volume (w/o PM)',
        format: 'currency',
        tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching'
    },
    { 
        value: 'orderCost', 
        label: 'Order Cost', 
        format: 'currency',
        tooltip: 'Total dollars paid to suppliers for parts ordered'
    },
    { 
        value: 'orderCount', 
        label: 'Order Count', 
        format: 'number',
        tooltip: 'Total number of orders'
    },
    { 
        value: 'avgOrderSize', 
        label: 'Avg. Order Size', 
        format: 'currency',
        tooltip: 'Total dollars of parts divided by total number of orders'
    },
    { 
        value: 'medianOrderSize', 
        label: 'Median Order Size', 
        format: 'currency',
        tooltip: 'Median of each order\'s total dollars of parts'
    },
    { 
        value: 'grossProfit', 
        label: 'Gross Profit', 
        format: 'currency',
        tooltip: 'Sum of all orders adjusted list price minus the buy price'
    },
    { 
        value: 'avgGrossMarginPct', 
        label: 'Avg. Gross Margin', 
        format: 'percent',
        tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price'
    },
    { 
        value: 'medianGrossMarginPct', 
        label: 'Median Gross Margin', 
        format: 'percent',
        tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)'
    },
    { 
        value: 'avgGrossMarginPctWoPM', 
        label: 'Avg. Gross Margin (w/o PM)', 
        format: 'percent',
        tooltip: 'Gross margins without list price adjustments for price matching'
    },
    { 
        value: 'medianGrossMarginPctWoPM', 
        label: 'Median Gross Margin (w/o PM)', 
        format: 'percent',
        tooltip: 'The median of the per-order gross margins without list price adjustments for price matching'
    },
    { 
        value: 'grossMarginLossFromPM', 
        label: 'Gross Margin Lost from PM', 
        format: 'percent',
        tooltip: 'The difference in the gross margin with and without list price adjustments from price matching'
    },
    { 
        value: 'partListPricePctOfOE', 
        label: 'Part List Price % of OE', 
        format: 'percent',
        tooltip: 'Adjusted list price divided by the original list price'
    },
    { 
        value: 'missedOpportunity', 
        label: 'Missed Opportunity', 
        format: 'currency',
        tooltip: 'Total list price minus the total “insurance best” for all parts ordered or confirmed. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)'
    },
    { 
        value: 'avgMissedOpportunity', 
        label: 'Avg. Missed Opportunity', 
        format: 'currency',
        tooltip: 'Total list price minus the total “insurance best”, divided by the number of orders with at least one eligible part. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)'
    },
    { 
        value: 'medianMissedOpportunity', 
        label: 'Median Missed Opportunity', 
        format: 'currency',
        tooltip: 'The median of the per-order missed opportunities, including eligible orders with $0. Each order contributes its total list price minus its total “insurance best”. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)'
    },
    { 
        value: 'pctAutoOrdered', 
        label: '% of Auto Orders Fully Ordered', 
        format: 'percent',
        tooltip: 'Percent of orders with auto order enabled and placed without flags (human review)'
    },
    { 
        value: 'pctAutoOrderEnabled', 
        label: 'Auto Order Usage', 
        format: 'percent',
        tooltip: 'Percent of orders with auto order enabled'
    },
    { 
        value: 'pctPartsFlagged', 
        label: '% of Parts Flagged', 
        format: 'percent',
        tooltip: 'Percent of parts that are flagged and held back from being auto ordered'
    },
    { value: 'pctPartsInvoiced', label: '% Parts Invoiced', format: 'percent', tooltip: 'Percent of ordered parts with a finalized invoice match (0–100).' },
];

/** Cost/Delivery variance — Vendors tab only (ENG-2352). */
const VENDOR_VARIANCE_CHART_METRICS: ChartMetricOption[] = [
    { value: 'totalCostVariance', label: 'Total Cost Variance', format: 'currency', tooltip: 'Sum of invoiced cost minus PO buy for matched parts that reached accounting. Positive = invoice higher than PO (overcharge).' },
    { value: 'avgCostVariance', label: 'Avg. Cost Variance', format: 'currency', tooltip: 'Average invoiced cost minus PO buy per matched part. Positive = invoice higher than PO (overcharge).' },
    { value: 'medianCostVariance', label: 'Median Cost Variance', format: 'currency', tooltip: 'Median invoiced cost minus PO buy per matched part. Positive = invoice higher than PO (overcharge).' },
    { value: 'avgDeliveryVarianceDays', label: 'Avg. Delivery Variance', format: 'days', tooltip: 'Average invoice day minus promised delivery day. Positive = arrived late vs promise.' },
    { value: 'medianDeliveryVarianceDays', label: 'Median Delivery Variance', format: 'days', tooltip: 'Median invoice day minus promised delivery day. Positive = arrived late vs promise.' },
];

const PARTS_INVOICED_CHART_METRIC: ChartMetricOption = {
    value: 'pctPartsInvoiced',
    label: '% Parts Invoiced',
    format: 'percent',
    tooltip: 'Percent of ordered parts with a finalized invoice match (0–100).',
};

// Vendor-specific metrics
export const VENDOR_CHART_METRIC_OPTIONS: ChartMetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', tooltip: 'Total dollars of parts divided by total number of orders' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', tooltip: 'Median of each order\'s total dollars of parts' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'avgGrossMarginPct', label: 'Avg. Discount', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Discount', format: 'percent', tooltip: 'The median of the per-order discounts' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Discount (w/o PM)', format: 'percent', tooltip: 'Discounts without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Discount (w/o PM)', format: 'percent', tooltip: 'The median of the per-order discounts without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Discount Lost from PM', format: 'percent', tooltip: 'The difference in the discount with and without list price adjustments from price matching' },
    { value: 'avgPartValue', label: 'Avg. Part Value', format: 'currency', tooltip: 'Average dollar value per part ordered' },
    { value: 'medianPartValue', label: 'Median Part Value', format: 'currency', tooltip: 'Median dollar value per part ordered' },
    { value: 'avgEtaChangeDays', label: 'Avg. ETA Change', format: 'days', tooltip: 'Average change in delivery ETA after purchase, in days. Positive = supplier pushed the ETA later, negative = earlier.' },
    { value: 'medianEtaChangeDays', label: 'Median ETA Change', format: 'days', tooltip: 'Median change in delivery ETA after purchase, in days. Positive = supplier pushed the ETA later, negative = earlier.' },
    ...VENDOR_VARIANCE_CHART_METRICS,
    PARTS_INVOICED_CHART_METRIC,
    { value: 'quoteRate', label: 'Quote Rate', format: 'percent', tooltip: 'Percent of quote opportunities where this vendor submitted a quote (after first observed quote on platform). Omitted when the vendor had no opportunities.' },
    { value: 'totalReturnCount', label: 'Return Count', format: 'number', tooltip: 'Total number of parts returned' },
    { value: 'totalReturnValue', label: 'Return Value', format: 'currency', tooltip: 'Total dollar value of parts returned' },
    { value: 'totalReturnPct', label: 'Total Return %', format: 'percent', tooltip: 'Percentage of parts returned out of total purchased' },
];

// Location-specific metrics
export const LOCATION_CHART_METRIC_OPTIONS: ChartMetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', tooltip: 'Total dollars of parts divided by total number of orders' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', tooltip: 'Median of each order\'s total dollars of parts' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', tooltip: 'Adjusted list price divided by the original list price' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best" for all parts ordered or confirmed. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best", divided by the number of orders with at least one eligible part. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', tooltip: 'The median of the per-order missed opportunities, including eligible orders with $0' },
    { value: 'avgPartValue', label: 'Avg. Part Value', format: 'currency', tooltip: 'Average dollar value per part ordered' },
    { value: 'medianPartValue', label: 'Median Part Value', format: 'currency', tooltip: 'Median dollar value per part ordered' },
    PARTS_INVOICED_CHART_METRIC,
    { value: 'totalReturnCount', label: 'Return Count', format: 'number', tooltip: 'Total number of parts returned' },
    { value: 'totalReturnValue', label: 'Return Value', format: 'currency', tooltip: 'Total dollar value of parts returned' },
    { value: 'totalReturnPct', label: 'Total Return %', format: 'percent', tooltip: 'Percentage of parts returned out of total purchased' },
    { value: 'pctOemVolume', label: '% OEM (Volume)', format: 'percent', tooltip: 'Percentage of order volume from OEM parts' },
    { value: 'pctOemCount', label: '% OEM (Count)', format: 'percent', tooltip: 'Percentage of order count from OEM parts' },
    { value: 'pctLkqVolume', label: '% LKQ (Volume)', format: 'percent', tooltip: 'Percentage of order volume from LKQ parts' },
    { value: 'pctLkqCount', label: '% LKQ (Count)', format: 'percent', tooltip: 'Percentage of order count from LKQ parts' },
    { value: 'pctAftermarketVolume', label: '% Aftermarket (Volume)', format: 'percent', tooltip: 'Percentage of order volume from aftermarket parts' },
    { value: 'pctAftermarketCount', label: '% Aftermarket (Count)', format: 'percent', tooltip: 'Percentage of order count from aftermarket parts' },
];

// Insurance-specific metrics
export const INSURANCE_CHART_METRIC_OPTIONS: ChartMetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', tooltip: 'Total dollars of parts divided by total number of orders' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', tooltip: 'Median of each order\'s total dollars of parts' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', tooltip: 'Adjusted list price divided by the original list price' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best" for all parts ordered or confirmed. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best", divided by the number of orders with at least one eligible part. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', tooltip: 'The median of the per-order missed opportunities, including eligible orders with $0' },
    { value: 'avgPartValue', label: 'Avg. Part Value', format: 'currency', tooltip: 'Average dollar value per part ordered' },
    { value: 'medianPartValue', label: 'Median Part Value', format: 'currency', tooltip: 'Median dollar value per part ordered' },
    PARTS_INVOICED_CHART_METRIC,
    { value: 'totalReturnCount', label: 'Return Count', format: 'number', tooltip: 'Total number of parts returned' },
    { value: 'totalReturnValue', label: 'Return Value', format: 'currency', tooltip: 'Total dollar value of parts returned' },
    { value: 'totalReturnPct', label: 'Total Return %', format: 'percent', tooltip: 'Percentage of parts returned out of total purchased' },
    { value: 'pctOemVolume', label: '% OEM (Volume)', format: 'percent', tooltip: 'Percentage of order volume from OEM parts' },
    { value: 'pctOemCount', label: '% OEM (Count)', format: 'percent', tooltip: 'Percentage of order count from OEM parts' },
    { value: 'pctLkqVolume', label: '% LKQ (Volume)', format: 'percent', tooltip: 'Percentage of order volume from LKQ parts' },
    { value: 'pctLkqCount', label: '% LKQ (Count)', format: 'percent', tooltip: 'Percentage of order count from LKQ parts' },
    { value: 'pctAftermarketVolume', label: '% Aftermarket (Volume)', format: 'percent', tooltip: 'Percentage of order volume from aftermarket parts' },
    { value: 'pctAftermarketCount', label: '% Aftermarket (Count)', format: 'percent', tooltip: 'Percentage of order count from aftermarket parts' },
];

// OEM-specific metrics
export const OEM_CHART_METRIC_OPTIONS: ChartMetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', tooltip: 'Total dollars of parts divided by total number of orders' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', tooltip: 'Median of each order\'s total dollars of parts' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', tooltip: 'Adjusted list price divided by the original list price' },
    { value: 'p90GrossMarginPct', label: 'Suggested Thresholds', format: 'percent', tooltip: '80% of orders for this OEM are placed above this gross margin' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best" for all parts ordered or confirmed. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best", divided by the number of orders with at least one eligible part. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', tooltip: 'The median of the per-order missed opportunities, including eligible orders with $0' },
    { value: 'avgPartValue', label: 'Avg. Part Value', format: 'currency', tooltip: 'Average dollar value per part ordered' },
    { value: 'medianPartValue', label: 'Median Part Value', format: 'currency', tooltip: 'Median dollar value per part ordered' },
    PARTS_INVOICED_CHART_METRIC,
    { value: 'totalReturnCount', label: 'Return Count', format: 'number', tooltip: 'Total number of parts returned' },
    { value: 'totalReturnValue', label: 'Return Value', format: 'currency', tooltip: 'Total dollar value of parts returned' },
    { value: 'totalReturnPct', label: 'Total Return %', format: 'percent', tooltip: 'Percentage of parts returned out of total purchased' },
    { value: 'pctOemVolume', label: '% OEM (Volume)', format: 'percent', tooltip: 'Percentage of order volume from OEM parts' },
    { value: 'pctOemCount', label: '% OEM (Count)', format: 'percent', tooltip: 'Percentage of order count from OEM parts' },
    { value: 'pctLkqVolume', label: '% LKQ (Volume)', format: 'percent', tooltip: 'Percentage of order volume from LKQ parts' },
    { value: 'pctLkqCount', label: '% LKQ (Count)', format: 'percent', tooltip: 'Percentage of order count from LKQ parts' },
    { value: 'pctAftermarketVolume', label: '% Aftermarket (Volume)', format: 'percent', tooltip: 'Percentage of order volume from aftermarket parts' },
    { value: 'pctAftermarketCount', label: '% Aftermarket (Count)', format: 'percent', tooltip: 'Percentage of order count from aftermarket parts' },
];

interface MetricGroup {
    label: string;
    avg: string;
    median: string;
}

export const METRIC_GROUPS: MetricGroup[] = [
    { label: 'Order Size', avg: 'avgOrderSize', median: 'medianOrderSize' },
    { label: 'Gross Margin', avg: 'avgGrossMarginPct', median: 'medianGrossMarginPct' },
    { label: 'Gross Margin (w/o PM)', avg: 'avgGrossMarginPctWoPM', median: 'medianGrossMarginPctWoPM' },
    { label: 'Missed Opportunity', avg: 'avgMissedOpportunity', median: 'medianMissedOpportunity' },
    { label: 'Part Value', avg: 'avgPartValue', median: 'medianPartValue' },
    { label: 'List Price', avg: 'avgListPrice', median: 'medianListPrice' },
];

// Vendor-specific metric groups (uses same underlying data but with different labels)
export const VENDOR_METRIC_GROUPS: MetricGroup[] = [
    { label: 'Order Size', avg: 'avgOrderSize', median: 'medianOrderSize' },
    { label: 'Discount', avg: 'avgGrossMarginPct', median: 'medianGrossMarginPct' },
    { label: 'Discount (w/o PM)', avg: 'avgGrossMarginPctWoPM', median: 'medianGrossMarginPctWoPM' },
    { label: 'Part Value', avg: 'avgPartValue', median: 'medianPartValue' },
    { label: 'ETA Change', avg: 'avgEtaChangeDays', median: 'medianEtaChangeDays' },
    { label: 'Cost Variance', avg: 'avgCostVariance', median: 'medianCostVariance' },
    { label: 'Delivery Variance', avg: 'avgDeliveryVarianceDays', median: 'medianDeliveryVarianceDays' },
];

/**
 * Format a number with K/M abbreviations
 * - Hundreds & below: no decimals
 * - Thousands: 1 decimal place with K
 * - Millions & beyond: no decimals with M
 */
const formatCompactNumber = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1000000) {
        return `${(value / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
    }
    if (absValue >= 1000) {
        return `${(value / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
    }
    return Math.round(value).toLocaleString();
};

/**
 * Format currency with K/M abbreviations
 * - Hundreds & below: no decimals ($500)
 * - Thousands: 1 decimal with K ($1.5K)
 * - Millions & beyond: 1 decimal with M ($1.2M)
 */
const formatCompactCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1000000) {
        return `${sign}$${(absValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}$${(absValue / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
    }
    return `${sign}$${Math.round(absValue).toLocaleString()}`;
};

/**
 * Format value for chart display (compact - used on axes and bar labels)
 */
export const formatChartValue = (value: number, format: 'currency' | 'percent' | 'number' | 'days'): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    
    switch (format) {
        case 'currency':
            return formatCompactCurrency(value);
        case 'percent':
            return `${(value || 0).toFixed(1)}%`;
        case 'days':
            return `${value > 0 ? '+' : ''}${(value || 0).toFixed(1)}d`;
        case 'number':
            return formatCompactNumber(value);
        default:
            return String(value);
    }
};

/**
 * Format value for tooltips (full precision)
 */
export const formatTooltipValue = (value: number, format: 'currency' | 'percent' | 'number' | 'days'): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    
    switch (format) {
        case 'currency':
            return new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'USD', 
                maximumFractionDigits: 0 
            }).format(value);
        case 'percent':
            return `${(value || 0).toFixed(1)}%`;
        case 'days': {
            const d = value || 0;
            const rounded = Math.abs(d) === 1 ? '1' : d.toFixed(1);
            return `${d > 0 ? '+' : ''}${rounded} ${Math.abs(d) === 1 ? 'day' : 'days'}`;
        }
        case 'number':
            return Math.round(value || 0).toLocaleString();
        default:
            return String(value);
    }
};

export const getMetricFormat = (
    metric: ChartMetricType, 
    isVendor: boolean = false,
    isLocation: boolean = false,
    isInsurance: boolean = false,
    isOEM: boolean = false
): 'currency' | 'percent' | 'number' | 'days' => {
    const options = isVendor 
        ? VENDOR_CHART_METRIC_OPTIONS 
        : isLocation 
            ? LOCATION_CHART_METRIC_OPTIONS 
            : isInsurance
                ? INSURANCE_CHART_METRIC_OPTIONS
                : isOEM
                    ? OEM_CHART_METRIC_OPTIONS
                    : CHART_METRIC_OPTIONS;
    const option = options.find(o => o.value === metric);
    // Fallback to default options if not found
    if (!option) {
        const fallbackOption = CHART_METRIC_OPTIONS.find(o => o.value === metric);
        return fallbackOption?.format || 'number';
    }
    return option.format;
};

export const getMetricLabel = (
    metric: ChartMetricType, 
    isVendor: boolean = false,
    isLocation: boolean = false,
    isInsurance: boolean = false,
    isOEM: boolean = false
): string => {
    const options = isVendor 
        ? VENDOR_CHART_METRIC_OPTIONS 
        : isLocation 
            ? LOCATION_CHART_METRIC_OPTIONS 
            : isInsurance
                ? INSURANCE_CHART_METRIC_OPTIONS
                : isOEM
                    ? OEM_CHART_METRIC_OPTIONS
                    : CHART_METRIC_OPTIONS;
    const option = options.find(o => o.value === metric);
    // Fallback to default options if not found
    if (!option) {
        const fallbackOption = CHART_METRIC_OPTIONS.find(o => o.value === metric);
        return fallbackOption?.label || metric;
    }
    return option.label;
};

interface ChartMetricSelectorProps {
    value: ChartMetricType;
    onChange: (metric: ChartMetricType) => void;
    label?: string;
    width?: string;
    isVendor?: boolean;
    isLocation?: boolean;
    isInsurance?: boolean;
    isOEM?: boolean;
}

const ChartMetricSelector = ({ value, onChange, label, width = '180px', isVendor = false, isLocation = false, isInsurance = false, isOEM = false }: ChartMetricSelectorProps) => {
    const sessionContext = useContext(SessionContext);
    const invoicingEnabled = orgShowsInvoicingReportingMetrics(sessionContext);
    // Domain-specific options
    const options = filterChartMetricsForInvoicing(
        isVendor 
            ? VENDOR_CHART_METRIC_OPTIONS 
            : isLocation 
                ? LOCATION_CHART_METRIC_OPTIONS 
                : isInsurance
                    ? INSURANCE_CHART_METRIC_OPTIONS
                    : isOEM
                        ? OEM_CHART_METRIC_OPTIONS
                        : CHART_METRIC_OPTIONS,
        invoicingEnabled,
    );
    const metricGroups = isVendor ? VENDOR_METRIC_GROUPS : METRIC_GROUPS;
    const selectedOption = options.find(o => o.value === value);
    const displayLabel = selectedOption?.label || 'Select Metric';

    // Identify if the current metric is part of a group
    const activeGroup = metricGroups.find(g => 
        (g.avg === value || g.median === value) &&
        // Ensure both options exist in the current options list
        options.some(o => o.value === g.avg) &&
        options.some(o => o.value === g.median)
    );

    // Prepare display options (grouping Avg/Median)
    const displayOptions = options.reduce((acc, option) => {
        const group = metricGroups.find(g => g.avg === option.value || g.median === option.value);
        
        // If part of a valid group (both variants exist in current options)
        if (group && options.some(o => o.value === group.avg) && options.some(o => o.value === group.median)) {
            // Only add group once
            if (!acc.find(o => o.isGroup && o.groupLabel === group.label)) {
                acc.push({
                    isGroup: true,
                    groupLabel: group.label,
                    value: group.avg!, // Default key, but we select the group
                    group: group,
                    tooltip: option.tooltip, // Take first tooltip
                    // Store reference to the currently selected metric if it's in this group
                    isSelected: value === group.avg || value === group.median
                });
            }
        } else {
            // Standalone option
            acc.push({
                isGroup: false,
                ...option,
                isSelected: value === option.value
            });
        }
        return acc;
    }, [] as any[]);

    return (
        <HStack spacing={2}>
            {label && <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">{label}</Text>}
            
            {/* Aggregation Selector (Avg vs Median) - only if active group */}
            {activeGroup && (
                <Menu placement="bottom-start">
                    <MenuButton
                        as={Button}
                        variant="outline"
                        bg="white"
                        borderColor="gray.300"
                        fontWeight="normal"
                        size="sm"
                        h="32px"
                        px={3}
                        minW="90px"
                        textAlign="left"
                        rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                        _hover={{ borderColor: 'gray.400' }}
                        _active={{ bg: 'white' }}
                    >
                        <Text fontSize="xs" noOfLines={1}>
                            {value === activeGroup.avg ? 'Avg.' : 'Median'}
                        </Text>
                    </MenuButton>
                    <MenuList p="6px" minW="140px" zIndex={2000}>
                        <MenuItemRow
                            kind="single"
                            isSelected={value === activeGroup.avg}
                            onClick={() => { if (activeGroup.avg) onChange(activeGroup.avg as ChartMetricType); }}
                        >
                            Avg.
                        </MenuItemRow>
                        <MenuItemRow
                            kind="single"
                            isSelected={value === activeGroup.median}
                            onClick={() => { if (activeGroup.median) onChange(activeGroup.median as ChartMetricType); }}
                        >
                            Median
                        </MenuItemRow>
                    </MenuList>
                </Menu>
            )}

            {/* Main Metric Selector */}
            <Menu placement="bottom-start">
                <MenuButton
                    as={Button}
                    variant="outline"
                    bg="white"
                    borderColor="gray.300"
                    fontWeight="normal"
                    size="sm"
                    h="32px"
                    px={3}
                    w={width}
                    textAlign="left"
                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                    _hover={{ borderColor: 'gray.400' }}
                    _active={{ bg: 'white' }}
                >
                    <Text fontSize="xs" noOfLines={1}>
                        {activeGroup ? activeGroup.label : displayLabel}
                    </Text>
                </MenuButton>
                <MenuList p="6px" minW="240px" zIndex={2000} maxH="350px" overflowY="auto">
                    {displayOptions.map((option: any) => (
                        <MenuItemRow
                            key={option.isGroup ? option.groupLabel : option.value}
                            kind="single"
                            isSelected={option.isSelected}
                            onClick={() => {
                                if (option.isGroup) {
                                    onChange(option.group.avg as ChartMetricType);
                                } else {
                                    onChange(option.value as ChartMetricType);
                                }
                            }}
                        >
                            {option.isGroup ? option.groupLabel : option.label}
                            {option.tooltip ? (
                                <Tooltip label={option.tooltip} placement="right" hasArrow>
                                    <Box
                                        as="span"
                                        display="inline-flex"
                                        verticalAlign="middle"
                                        ml={2}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <Icon as={TbInfoCircle as React.ElementType} boxSize={3} />
                                    </Box>
                                </Tooltip>
                            ) : null}
                        </MenuItemRow>
                    ))}
                </MenuList>
            </Menu>
        </HStack>
    );
};

export default ChartMetricSelector;
